/* eslint-disable require-await */
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, Observable, ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';

import { Loggable } from '../../../utils/logging/loggable';
import { PRESENTATION_V3_TIMEOUTS, PresentationTransitions, selectors } from '../state/app';

import { PresentationAudioService } from './presentation/presentation-audio.service';
import { SceneService } from './scene.service';

@Injectable({
  providedIn: 'root',
})
export class TransitionPresentationService extends Loggable implements OnDestroy {
  public showIconOverlay = false;
  public showFullOverlay = false;
  public showFadeBackground = false;
  public showFadeInBackground = false;
  private _overlayPresentationText = '';

  // Indicates that the background is altered (faded out)
  public isAltered = false;

  private _isTransitioning: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isTransitioning$: Observable<boolean> = this._isTransitioning.asObservable();

  private destroyed$ = new ReplaySubject<boolean>(1);

  public transitionTimers: PresentationTransitions = PRESENTATION_V3_TIMEOUTS;

  public constructor(
    private store: Store,
    public sceneService: SceneService,
    public presentationAudioService: PresentationAudioService,
  ) {
    super();
    this.setDisplayName('TransitionPresentationService');

    this.store
      .select(selectors.getTransitionTimers)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((transitionTimers) => {
        this.transitionTimers = transitionTimers;
      });
  }

  /**
   * Enable transition mode effect
   *
   * @param {string} overlayText Text displayed in the transition screen (if it will be called), should be member of LoadingTransitionLabel
   */
  public async executeTransitionToPresentation(overlayText: string): Promise<boolean> {
    this._isTransitioning.next(true);
    this._overlayPresentationText = overlayText;

    // Start fading audio, no await to fade background at same time
    void this.presentationAudioService.transitionFadeOutAudio(this.transitionTimers.audioFadeTimeout);
    //Fade out background
    await this.enableFadeOutBackground();
    //Fade in Presentation Icon
    await this.enableFadeInTransitionIcon();
    // Enable full transition overlay
    this._isTransitioning.next(true);
    await this.enableFullTransitionOverlay();

    this.presentationAudioService.resetFadedAudioMix();

    this._isTransitioning.next(false);
    this.showFadeBackground = false;
    this.showIconOverlay = false;

    return true;
  }

  public async enableFadeInTransitionIcon(): Promise<boolean> {
    this.showIconOverlay = true;

    // Hold transition for some time
    await new Promise((resolve) => {
      setTimeout(async () => {
        resolve(true);
      }, this.transitionTimers.presentationIconFadeIn);
    });
    return true;
  }

  private async enableFullTransitionOverlay(): Promise<boolean> {
    this.showFullOverlay = true;

    // Hold transition for some time
    await new Promise((resolve) => {
      setTimeout(async () => {
        this.showFullOverlay = false;
        resolve(true);
      }, this.transitionTimers.transitionLoadTimeout);
    });
    return true;
  }

  private async enableFadeOutBackground(): Promise<void> {
    this.showFadeBackground = true;

    await new Promise((resolve) => {
      setTimeout(async () => {
        this.isAltered = true;
        resolve(true);
      }, this.transitionTimers.backgroundFadeOutTimeout);
    });
  }

  public async enableFadeInBackground(): Promise<void> {
    if (this.isAltered) {
      this.showFadeInBackground = true;

      await new Promise((resolve) => {
        setTimeout(async () => {
          this.showFadeInBackground = false;
          this.isAltered = false;
          resolve(true);
        }, this.transitionTimers.backgroundFadeInTimeout);
      });
    }
  }

  public set overlayPresentationText(t: string) {
    this._overlayPresentationText = t;
  }

  public get overlayPresentationText(): string {
    return this._overlayPresentationText;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
